/**
 * R232q72: AIWARI PWA install pill.
 *
 * Shows a small floating "Установить AIWARI" button on the AuthScreen +
 * RealmSelect stages. Behaviour:
 *  - Android Chrome / Edge: интерактив `beforeinstallprompt` → клик по pill
 *    вызывает нативный prompt, после `userChoice` пилюля прячется навсегда.
 *  - iOS Safari (нет beforeinstallprompt): показываем разовый toast с
 *    инструкцией "Поделиться → На экран Домой" + кнопкой OK. Решение юзера
 *    запоминается в localStorage, чтобы не приставать каждый заход.
 *  - Уже установленное приложение (display-mode: standalone | minimal-ui |
 *    fullscreen | iOS legacy `navigator.standalone`): компонент рендерит
 *    null, чтобы не мешать HUD.
 *
 * Wiring: в `app.tsx` рендерится только когда stage ∈ { 'auth', 'splash',
 * 'realm-select' }, чтобы не перекрывать игровой HUD.
 */

import { useEffect, useState } from 'react';

const LS_DISMISSED = 'aiwari-pwa-install-dismissed';
const LS_IOS_TOAST_SHOWN = 'aiwari-pwa-ios-toast-seen';

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;
  } catch { /* swallow */ }
  // iOS Safari legacy.
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

export function PwaInstallPrompt() {
  const [bip, setBip] = useState<BIPEvent | null>(null);
  const [showIosToast, setShowIosToast] = useState(false);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    if (installed) return;
    if (localStorage.getItem(LS_DISMISSED) === '1') return;

    const onBip = (e: Event) => {
      e.preventDefault();
      setBip(e as BIPEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setBip(null);
      localStorage.setItem(LS_DISMISSED, '1');
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);

    // iOS one-time toast.
    if (isIos() && localStorage.getItem(LS_IOS_TOAST_SHOWN) !== '1') {
      const t = window.setTimeout(() => setShowIosToast(true), 1200);
      return () => {
        window.clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBip);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  if (installed) return null;

  const onInstallClick = async () => {
    if (!bip) return;
    try {
      await bip.prompt();
      const choice = await bip.userChoice;
      if (choice.outcome === 'accepted') {
        localStorage.setItem(LS_DISMISSED, '1');
      }
    } catch { /* swallow */ }
    setBip(null);
  };

  const dismissIos = () => {
    localStorage.setItem(LS_IOS_TOAST_SHOWN, '1');
    setShowIosToast(false);
  };

  const pillStyle: React.CSSProperties = {
    position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
    padding: '10px 18px', borderRadius: 999,
    background: 'linear-gradient(135deg, #C9A96A 0%, #8C6F3D 100%)',
    color: '#0B0907', fontWeight: 700, fontSize: 14, letterSpacing: 0.3,
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.45)', cursor: 'pointer',
    fontFamily: 'Cinzel, serif',
  };
  const toastStyle: React.CSSProperties = {
    position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)',
    zIndex: 9999, maxWidth: 340, padding: '14px 18px', borderRadius: 12,
    background: 'rgba(11, 14, 23, 0.96)', color: '#E5EAF5',
    border: '1px solid #C9A96A', boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
    fontSize: 13, lineHeight: 1.45,
  };

  return (
    <>
      {bip && (
        <button type="button" style={pillStyle} onClick={onInstallClick}>
          Установить AIWARI
        </button>
      )}
      {showIosToast && (
        <div style={toastStyle} role="dialog" aria-live="polite">
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#C9A96A' }}>
            Установить AIWARI на iPhone
          </div>
          <div>Нажмите «Поделиться» <span aria-hidden>⬆</span> → «На экран Домой».</div>
          <button
            type="button"
            onClick={dismissIos}
            style={{
              marginTop: 10, padding: '6px 14px', borderRadius: 8,
              background: '#C9A96A', color: '#0B0907', border: 'none',
              fontWeight: 700, cursor: 'pointer',
            }}
          >Понятно</button>
        </div>
      )}
    </>
  );
}
